import React from "react";
import Moment from "react-moment";
import { Phone, Mail, MapPin, ShieldCheck, Clock, Briefcase, Building2, UserCog } from "lucide-react";
import "./ViewUser.css";

const sectionTitleStyle = {
  fontSize: "12px",
  fontWeight: 700,
  color: "#94A3B8",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  borderBottom: "1px solid #EEF2F6",
  paddingBottom: "8px",
  marginBottom: "14px",
};

const labelStyle = {
  display: "block",
  fontSize: "12px",
  fontWeight: 700,
  color: "#2E6DA4",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: "4px",
};

const valueStyle = {
  fontSize: "12px",
  fontWeight: 600,
  color: "#0F2D42",
  lineHeight: 1.5,
};

const fieldGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "18px",
};

const UserInfo = ({ userData }) => {
  if (!userData) {
    return (
      <div className="usv-page">
        <div className="usv-card usv-empty">No user data available.</div>
      </div>
    );
  }

  const fullName = `${userData.first_name || ""} ${userData.middle_name || ""} ${userData.last_name || ""}`
    .replace(/\s+/g, " ")
    .trim();

  const roleNames = (userData.user_role_map || [])
    .map((r) => r.roleEntity?.name)
    .filter(Boolean)
    .join(", ");

  const regionNames = (userData.user_region_map || [])
    .map((r) => r.regionName)
    .filter(Boolean)
    .join(", ");

  return (
    <div className="usv-page">
      <div className="usv-card usv-card-body" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {/* ── Basic Information ── */}
        <div>
          <div style={sectionTitleStyle}>Basic Information</div>
          <div style={fieldGridStyle}>
            <div>
              <label style={labelStyle}>Name</label>
              <div style={valueStyle}>{fullName || "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>User Name</label>
              <div style={valueStyle}>{userData.user_name || "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>
                <Mail size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                User Email
              </label>
              <div style={valueStyle}>{userData.notification_email || userData.email || "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>
                <Clock size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                Created On
              </label>
              <div style={valueStyle}>
                {userData.createdOn ? (
                  <Moment format="DD-MM-YYYY, hh:mm A">{userData.createdOn}</Moment>
                ) : (
                  "NA"
                )}
              </div>
            </div>
            <div>
              <label style={labelStyle}>
                <Clock size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                Updated On
              </label>
              <div style={valueStyle}>
                {userData.updatedOn ? (
                  <Moment format="DD-MM-YYYY, hh:mm A">{userData.updatedOn}</Moment>
                ) : (
                  "NA"
                )}
              </div>
            </div>
            <div>
              <label style={labelStyle}>
                <UserCog size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                Created By
              </label>
              <div style={valueStyle}>{userData.createdByName || "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>
                <UserCog size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                Updated By
              </label>
              <div style={valueStyle}>{userData.updatedByName || "NA"}</div>
            </div>
          </div>
        </div>

        {/* ── Contact Information ── */}
        <div>
          <div style={sectionTitleStyle}>Contact Information</div>
          <div style={fieldGridStyle}>
            <div>
              <label style={labelStyle}>
                <Phone size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                Primary Phone No.
              </label>
              <div style={valueStyle}>{userData.phone_number || "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>
                <Phone size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                Secondary Phone No.
              </label>
              <div style={valueStyle}>{userData.secondary_phone_number || "NA"}</div>
            </div>
          </div>
        </div>

        {/* ── Access ── */}
        <div>
          <div style={sectionTitleStyle}>Access</div>
          <div style={fieldGridStyle}>
            <div>
              <label style={labelStyle}>
                <ShieldCheck size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                Role
              </label>
              <div style={valueStyle}>{roleNames || "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>
                <MapPin size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                Region
              </label>
              <div style={valueStyle}>{regionNames || "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>
                <Building2 size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                Agency
              </label>
              <div style={valueStyle}>{userData.agencyName || userData.agency_name || userData.agency || "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>
                <Briefcase size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                Job Title
              </label>
              <div style={valueStyle}>{userData.jobTitleName || userData.job_title || userData.job_title_name || "NA"}</div>
            </div>
          </div>
        </div>

        {/* ── Address ── */}
        <div>
          <div style={sectionTitleStyle}>Address</div>
          <div
            style={{
              ...valueStyle,
              fontWeight: 500,
              background: "#EBF4FA",
              border: "1px solid #E2E8F0",
              borderRadius: "10px",
              padding: "12px 14px",
            }}
          >
            {userData.address || "NA"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserInfo;
